import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useBranding } from '../contexts/BrandingContext';

function AdminManageSports() {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#667eea';
  const token = localStorage.getItem('adminToken');
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/sports?show_all=true`);
      const data = await response.json();
      setSports(Array.isArray(data) ? data : (data.sports || []));
    } catch (err) {
      setError('Failed to load sports');
      setSports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'name' && !editingId) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const url = editingId
      ? `${API_URL}/sports/${editingId}`
      : `${API_URL}/sports`;

    const method = editingId ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save sport');
      }

      setSuccess(editingId ? 'Sport updated!' : 'Sport created!');
      resetForm();
      fetchSports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (sport) => {
    setEditingId(sport.id);
    setFormData({
      name: sport.name,
      slug: sport.slug,
      description: sport.description || '',
      is_active: sport.is_active
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleToggleActive = async (sport) => {
    try {
      const response = await fetch(`${API_URL}/sports/${sport.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: sport.name,
          slug: sport.slug,
          description: sport.description,
          is_active: !sport.is_active
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      setSuccess(`Sport ${!sport.is_active ? 'enabled' : 'disabled'}!`);
      fetchSports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (sport) => {
    if (sport.court_count > 0) {
      setError(`Cannot delete "${sport.name}" - it has ${sport.court_count} court(s).`);
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!window.confirm(`Delete "${sport.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/sports/${sport.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      setSuccess('Sport deleted!');
      fetchSports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', slug: '', description: '', is_active: true });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading sports...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="admin-btn admin-btn-primary"
        >
          {showForm ? 'Cancel' : '+ Add Sport'}
        </button>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="admin-card" style={{ marginBottom: '16px' }}>
          <div className="admin-card-header">{editingId ? 'Edit Sport' : 'Add New Sport'}</div>
          <div className="admin-card-body">
            <div className="admin-form-row">
              <div className="admin-field">
                <label className="admin-label">Sport Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                  placeholder="e.g., Badminton"
                />
              </div>
              <div className="admin-field">
                <label className="admin-label">Slug *</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  className="admin-input"
                  placeholder="auto-generated"
                />
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Auto-generated from name</p>
              </div>
              <div className="admin-field">
                <label className="admin-label">Status</label>
                <select
                  name="is_active"
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                  className="admin-select"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="admin-field" style={{ marginTop: '12px' }}>
              <label className="admin-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                className="admin-input"
                style={{ resize: 'vertical' }}
                placeholder="Brief description of the sport"
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={resetForm} className="admin-btn admin-btn-secondary">Cancel</button>
              <button onClick={handleSubmit} className="admin-btn admin-btn-primary">
                {editingId ? 'Update Sport' : 'Add Sport'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sports List */}
      <div className="admin-card">
        <div className="admin-card-header">Sports ({sports.length})</div>

        {sports.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">🏸</div>
            <p>No sports found</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Courts</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sports.map((sport) => (
                  <tr key={sport.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '14px'
                        }}>
                          {sport.name.charAt(0)}
                        </div>
                        <span style={{ fontWeight: 600 }}>{sport.name}</span>
                      </div>
                    </td>
                    <td style={{ color: '#6b7280' }}>{sport.slug}</td>
                    <td>
                      <span className="admin-badge admin-badge-info">{sport.court_count || 0}</span>
                    </td>
                    <td>
                      <span className={`admin-badge ${sport.is_active ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                        {sport.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => handleEdit(sport)} className="admin-btn admin-btn-primary admin-btn-sm">
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleActive(sport)}
                          className={`admin-btn admin-btn-sm ${sport.is_active ? 'admin-btn-secondary' : 'admin-btn-primary'}`}
                        >
                          {sport.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDelete(sport)}
                          disabled={sport.court_count > 0}
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          style={sport.court_count > 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          title={sport.court_count > 0 ? `Cannot delete - has ${sport.court_count} court(s)` : 'Delete sport'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminManageSports;
